import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import Select from "react-select";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";

import Toggle from "../../../../Components/Common/Toggle";
import Styles from "./Styles.module.css";
import MenuApi from "../../../../App/api/menu.apis";
import DeliveryInfoApi from "../../../../App/api/deliveryinfo.api";
import GradientButton from "../../../../Components/Button/GradientButton";
import SelectDropdown from "../../../../Components/Dropdown/SelectDropdown";
import Tooltip from "../../../../Components/Tooltip/Tooltip";
import { FormField } from "../../../../Components/FormField";
import { useFetchAll } from "../../../../custom/FetchApiHook";
import selectDropdown from "../../../../custom/SelectDropdown";

function CityModal({
  selectedCity,
  cityData,
  setCityData,
  selectedCountry,
  updateCityList,
  onAddCity,
}) {
  const validationSchema = Yup.object({
    country: Yup.string()
      .required("Country is required")
      .typeError("Please select a country"),
    city: Yup.string()
      .required("City is required")
      .typeError("Please select a city"),
  });

  console.log("slected city", selectedCity);

  const { theme } = useSelector((state) => state.theme);
  const countryList = useSelector((state) => state.country.data);

  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });
  const [cityId, setCityId] = useState(null);
  const [countryId, setCountryId] = useState(
    selectedCountry?.country?._id || null
  );

  const { value: cityData1, fetchAllData: fetchCities } = useFetchAll(() =>
    countryId ? MenuApi.getAllCity(countryId) : null
  );
  const { value: deliveryData } = useFetchAll(
    DeliveryInfoApi.getAllDeliveryInfo
  );

  const cityOptions = selectDropdown(cityData1?.data, "_id", "name");
  const deliveryInfo = deliveryData?.data || [];

  const getCoordinates = async (cityName) => {
    try {
      const response = await MenuApi.getCityCoordinates(cityName);
      return response.data.results[0].geometry;
    } catch (error) {
      console.error("Error fetching city coordinates:", error);
    }
  };

  const handleCountryChange = async (selected, setFieldValue) => {
    setCountryId(selected);
    setFieldValue("country", selected);
    setCityData([]);
  };

  const handleCityChange = async (cityId, setFieldValue) => {
    const selectedCity = cityData1?.data?.find((city) => city._id === cityId);
    if (selectedCity) {
      const coords = {
        lat: selectedCity?.latitude,
        lng: selectedCity?.longitude,
      };
      if (coords) {
        setCoordinates(coords);
        setFieldValue("latitude", coords.lat);
        setFieldValue("longitude", coords.lng);
        setCityId(selectedCity._id);
        setFieldValue("city", cityId);
      }
    }
  };

  const splitDeliveryInfo = () => {
    const middleIndex = Math.ceil(deliveryInfo.length / 2);
    return {
      firstColumn: deliveryInfo.slice(0, middleIndex),
      secondColumn: deliveryInfo.slice(middleIndex),
    };
  };
  const { firstColumn, secondColumn } = splitDeliveryInfo();

  useEffect(() => {
    if (countryId) {
      fetchCities();
    }
  }, [countryId]);

  useEffect(() => {
    const fetchCoordinates = async () => {
      if (selectedCity?.name) {
        const coords = await getCoordinates(selectedCity.name);
        if (coords) {
          setCoordinates(coords);
        }
      }
    };
    fetchCoordinates();
  }, [selectedCity]);

  const initialFormValues = {
    country: selectedCity?.country?._id || countryId || "",
    city: selectedCity?.city?._id || cityId || "",
    latitude: coordinates.lat || "",
    longitude: coordinates.lng || "",
    business: selectedCity?.business || false,
    advertise_available: selectedCity?.advertise_available || false,
    show_advertise: selectedCity?.show_advertise || false,
  };

  const handleSubmit = async (values, { resetForm }) => {
    console.log("values", values);

    try {
      let response;
      if (!selectedCity) {
        response = await MenuApi.createCityInfo(values);
        toast.success("City details created successfully!");
        if (response?.data) onAddCity(response.data);
      } else {
        console.log("values:", values);
        response = await MenuApi.updateCityInfo(selectedCity._id, values);
        toast.success("City details updated successfully!");
      }
      resetForm();
    } catch (error) {
      console.error("Error submitting city details:", error);
      toast.error("An error occurred while submitting city details.");
    }
  };

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue }) => (
        <Form>
          <div
            className={`p-6 h-full w-4/6  shadow-md rounded-md ${
              theme === "light"
                ? "hover:bg-[#f8f8f8]"
                : "hover:bg-thirsaryColor"
            }`}
          >
            <h1 className={Styles.text_head}>Basic Details</h1>
            <hr className="w-20 h-2 mb-4" />

            {/* Country Dropdown */}
            <FormField
              label="Country"
              tooltip="Select a country"
              className="text-base"
            >
              <SelectDropdown
                value={values.country}
                placeholder={selectedCity?.country?.name || "Select country"}
                options={countryList || []}
                optionLabel="name"
                optionValue="_id"
                handleFilter={(name, value) =>
                  handleCountryChange(value, setFieldValue)
                }
              />
              <ErrorMessage name="country" component="p" className="error" />
            </FormField>

            {/* City Dropdown */}
            <FormField
              label="City"
              tooltip="Select a city"
              className="text-base"
            >
              <SelectDropdown
                value={selectedCity?.city?.name || values.city}
                placeholder={selectedCity?.city?.name || "Select City"}
                options={cityOptions || []}
                optionLabel="label"
                optionValue="value"
                handleFilter={(name, value) =>
                  handleCityChange(value, setFieldValue)
                }
              />
              <ErrorMessage name="city" component="p" className="error" />
            </FormField>

            {/* Coordinates */}
            <div className="flex gap-x-5 mt-8 mb-5">
              <span className="flex flex-col">
                <input
                  type="text"
                  placeholder="Latitude"
                  value={selectedCity?.city?.latitude || coordinates.lat || ""}
                  className="h-10 text-base font-bold p-2 bg-gray-200 border rounded"
                  disabled
                />
                <ErrorMessage name="latitude" component="p" className="error" />
              </span>

              <span className="flex flex-col">
                <input
                  type="text"
                  placeholder="Longitude"
                  value={selectedCity?.city?.longitude || coordinates.lng || ""}
                  className="h-10 text-base font-bold p-2 bg-gray-200 border rounded"
                  disabled
                />
                <ErrorMessage
                  name="longitude"
                  component="p"
                  className="error"
                />
              </span>
            </div>

            {/* Toggles */}
            <div className={Styles.column_cont}>
              <span className="mt-8 flex flex-row">
                <Toggle
                  value={values.business}
                  onChange={() => setFieldValue("business", !values.business)}
                  disabled
                />

                <span className="flex flex-col">
                  <span className="flex flex-row">
                    <label className="text-lg pl-2  capitalize font-bold">
                      Business
                    </label>
                    <span>
                      <Tooltip
                        text="status of business is active or not"
                        position="right"
                      />
                    </span>
                  </span>

                  <p className={Styles.text}>Business is active or not</p>
                </span>
              </span>
              {/* -------------- */}
              <span className="mt-8 flex flex-row ">
                <Toggle
                  value={values.advertise_available}
                  onChange={() =>
                    setFieldValue(
                      "advertise_available",
                      !values.advertise_available
                    )
                  }
                  disabled
                />
                <span className="flex flex-col">
                  <span className="flex flex-row">
                    <label className="text-lg  pl-2  capitalize font-bold">
                      Advertise Available
                    </label>
                    <span>
                      <Tooltip text="Advertise is Available" position="right" />
                    </span>
                  </span>
                  <p className={Styles.text}>
                    If order from this city and advertise is ON, user can apply
                    this
                  </p>
                </span>
              </span>
              {/* -------------- */}

              <span className="mt-10 flex flex-row">
                <Toggle
                  value={values.show_advertise}
                  onChange={() =>
                    setFieldValue("show_advertise", !values.show_advertise)
                  }
                  disabled
                />
                <span className="flex flex-col">
                  <span className="flex flex-row">
                    <label className="text-lg  pl-2 capitalize font-bold">
                      Show Advertise
                    </label>
                    <span>
                      <Tooltip
                        text=" want to show advertise"
                        position="right"
                      />
                    </span>
                  </span>
                  <p className={Styles.text}>
                    If on and any ads available, it will be sent to the user
                  </p>
                </span>
              </span>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-4">
              <GradientButton
                type="submit"
                label={selectedCity ? "Update" : "Submit"}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default CityModal;
